import {
  FieldValue,
  arrayRemove,
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  updateDoc
} from "firebase/firestore";
import { useCollectionData, useDocumentData } from "react-firebase-hooks/firestore";
import { db } from "./clientApp"; // Ensure this imports the correct Firestore instance
import { isDev } from "app/general";

const NOTIFICATIONS_DB_REFERENCE = "Notifications";
const MESSAGES_LAST_DB_REFERENCE = "LastMessages";

export interface MessageType {
  text: string;
  createdAt: number;
  uid: string;
  isSender: boolean;
}

export enum NotificationType {
  MESSAGE = "MESSAGE",
  LIKED_POST = "LIKED_POST",
  LIKED_COMMENT = "LIKED_COMMENT",
  LIKED_EVENT = "LIKED_EVENT",
  COMMENTED_POST = "COMMENTED_POST",
  FRIEND_REQUEST = "FRIEND_REQUEST",
  FRIEND_ACCEPTED = "FRIEND_ACCEPTED",
  EVENT_INVITE = "EVENT_INVITE",
  EVENT_ACCEPTED = "EVENT_ACCEPTED",
  EVENT_REJECTED = "EVENT_REJECTED",
  EVENT_REMOVED = "EVENT_REMOVED",
  COMMUNITY_INVITE = "COMMUNITY_INVITE",
  COMMUNITY_ACCEPTED = "COMMUNITY_ACCEPTED",
  COMMUNITY_REJECTED = "COMMUNITY_REJECTED",
  COMMUNITY_REMOVED = "COMMUNITY_REMOVED",
  FLAGGED_POST = "FLAGGED_POST",
  FLAGGED_EVENT = "FLAGGED_EVENT",
  FLAGGED_COMMUNITY = "FLAGGED_COMMUNITY",
  FLAGGED_USER = "FLAGGED_USER",
  OTHER = "OTHER"
}

export interface NotificationInstance {
  uid: string;
  text: string;
  entryId?: string;
  createdAt: number;
  type: NotificationType;
}

export interface NotificationInstanceDB {
  Uid: string;
  Text: string;
  EntryId?: string;
  CreatedAt: number;
  Type: NotificationType;
}

export const removeNotification = async (uid: string, notification: NotificationInstanceDB) => {
  if (!uid) return;

  const notifDocRef = doc(db, NOTIFICATIONS_DB_REFERENCE, uid);
  try {
    await updateDoc(notifDocRef, {
      NotificationsList: arrayRemove(notification)
    });
  } catch (error) {
    isDev && console.error("Error removing notification:", error);
  }
}

export const removeNotifications = async (uid: string, notifications: NotificationInstanceDB[]) => {
  if (!uid) return;

  const notifDocRef = doc(db, NOTIFICATIONS_DB_REFERENCE, uid);
  try {
    await updateDoc(notifDocRef, {
      NotificationsList: arrayRemove(...notifications)
    });
  } catch (error) {
    isDev && console.error("Error removing notifications:", error);
  }
}

export const getMyNotifications = async (uid: string | undefined, setNotifications: (notificationsList: NotificationInstanceDB[]) => void) => {
  if (!uid) return;

  const notifDocRef = doc(db, NOTIFICATIONS_DB_REFERENCE, uid);
  const docSnap = await getDoc(notifDocRef);

  if (!docSnap.exists()) {
    return [];
  }

  const unsub = onSnapshot(notifDocRef, (doc: any) => {
    const notifData = doc.data();
    setNotifications(notifData?.NotificationsList || []);
  });

  return unsub;
}

export const getLastMessages = async (uid: string, setLastMessages: (messages: { [key: string]: MessageType }) => void) => {
  if (!uid) return;

  const lastMessagesDocRef = doc(db, MESSAGES_LAST_DB_REFERENCE, uid);
  const docSnap = await getDoc(lastMessagesDocRef);

  if (!docSnap.exists()) {
    return [];
  }

  const unsub = onSnapshot(lastMessagesDocRef, (doc: any) => {
    const data = doc.data();
    setLastMessages(data || {});
  });

  return unsub;
}

export const useMyNotifications = (uid: string) => {
  if (!uid) return;

  const ref = doc(db, NOTIFICATIONS_DB_REFERENCE, uid);
  const [data, isLoading, error] = useDocumentData(ref);

  return data?.NotificationsList || [];
}

export const addNotification = async (uid: string, notification: NotificationInstanceDB) => {
  if (isDev) {
    console.log("addNotification", uid, notification);
  }

  if (!uid) return;

  try {
    const notifDocRef = doc(db, NOTIFICATIONS_DB_REFERENCE, uid);
    await updateDoc(notifDocRef, {
      NotificationsList: arrayUnion(notification)
    });
  } catch (error) {
    isDev && console.error("Error adding notification:", error);
  }
}

export const addToLastMessages = async (uid: string, otherUid: string, message: { text: string; createdAt: number }) => {
  if (!uid) return;

  const messageDocRef = doc(db, MESSAGES_LAST_DB_REFERENCE, uid);
  try {
    await updateDoc(messageDocRef, {
      [otherUid]: {
        ...message,
        isSender: true
      }
    });
  } catch (error) {
    isDev && console.error("Error updating last message for uid:", error);
  }

  const messageOtherDocRef = doc(db, MESSAGES_LAST_DB_REFERENCE, otherUid);
  try {
    await updateDoc(messageOtherDocRef, {
      [uid]: {
        ...message,
        isSender: false
      }
    });
  } catch (error) {
    isDev && console.error("Error updating last message for otherUid:", error);
  }
}

export const groupNotificationsByTypeAndUid = (notifications: NotificationInstanceDB[]) => {
  const grouped: { [key: string]: NotificationInstanceDB } = {};

  for (let n of notifications.reverse()) {
    const key = `${n.Type}-${n.Uid}-${n.EntryId}`;
    if (!grouped[key]) grouped[key] = n;
  }

  return Object.values(grouped);
}
